import type {
  ScheduleCompareData,
  ScheduleVerificationItem,
  VerificationStatus,
} from '@/types/api';

/** Códigos/mensajes que indican verificación no disponible (backend ausente o respuesta inválida) */
const SOFT_FAILURE_CODES = new Set([
  'HTTP_404',
  'INVALID_JSON',
  'EMPTY_RESPONSE',
  'NOT_FOUND',
  'NETWORK_ERROR',
]);

export function isSoftVerificationFailure(code?: string, message?: string): boolean {
  if (code && SOFT_FAILURE_CODES.has(code)) return true;
  if (code?.startsWith('HTTP_4') || code?.startsWith('HTTP_5')) return true;
  const msg = (message || '').toLowerCase();
  return (
    msg.includes('json') ||
    msg.includes('not found') ||
    msg.includes('404') ||
    msg.includes('no es json')
  );
}

export const SCHEDULE_VERIFICATION_ALARM_TYPES = [
  'schedule_mismatch',
  'schedule_missing_device',
  'schedule_extra_device',
  'schedule_verification_err',
] as const;

export type ScheduleVerificationAlarmType =
  (typeof SCHEDULE_VERIFICATION_ALARM_TYPES)[number];

export function isScheduleVerificationAlarmType(type: string): boolean {
  return (SCHEDULE_VERIFICATION_ALARM_TYPES as readonly string[]).includes(type);
}

/** Incidencia real: plataforma ≠ dispositivo o error de lectura */
export function isVerificationIssueStatus(
  status?: VerificationStatus | null
): boolean {
  return status === 'mismatch' || status === 'missing' || status === 'error';
}

/** Aún no hay contraste definitivo con el dispositivo */
export function isVerificationInProgressStatus(
  status?: VerificationStatus | null
): boolean {
  return status === 'pending' || status === 'sent' || status === 'confirmed';
}

export function isVerificationOkStatus(
  status?: VerificationStatus | null
): boolean {
  return status === 'verified';
}

export type VerificationAction =
  | 'syncToDevice'
  | 'importFromDevice'
  | 'verify'
  | 'retry';

/** Acciones disponibles según estado (alineado con backend v0.7) */
export function getVerificationActions(
  status: VerificationStatus
): VerificationAction[] {
  switch (status) {
    case 'verified':
      return [];
    case 'confirmed':
      return ['verify'];
    case 'mismatch':
      return ['syncToDevice', 'importFromDevice', 'verify'];
    case 'missing':
      return ['syncToDevice'];
    case 'pending':
      // Verificar a nivel controladora (cola LoRa); en ítem solo enviar
      return ['syncToDevice'];
    case 'sent':
      return ['verify'];
    case 'error':
      return ['retry', 'verify'];
    default:
      return ['verify'];
  }
}

/**
 * Texto del lado dispositivo. Nunca usar "No encontrada" en pending/sent/confirmed:
 * eso implica missing tras una verificación real.
 */
export function getDeviceSideMessage(
  item: ScheduleVerificationItem
): { kind: 'data' | 'empty'; text: string } {
  if (item.deviceSchedule) {
    return { kind: 'data', text: '' };
  }

  switch (item.verificationStatus) {
    case 'missing':
      return {
        kind: 'empty',
        text: 'No encontrada en el dispositivo',
      };
    case 'pending':
      return {
        kind: 'empty',
        text: 'Pendiente de verificar / en cola LoRa. Puede cerrar y volver más tarde.',
      };
    case 'sent':
      return {
        kind: 'empty',
        text: 'Enviada; esperando confirmación del dispositivo.',
      };
    case 'confirmed':
      return {
        kind: 'empty',
        text: 'Confirmada por el dispositivo; falta contraste completo.',
      };
    case 'error':
      return {
        kind: 'empty',
        text: 'Error al leer el dispositivo. Reintente la verificación.',
      };
    case 'mismatch':
      return {
        kind: 'empty',
        text: 'Sin datos de dispositivo en esta respuesta.',
      };
    default:
      return {
        kind: 'empty',
        text: 'Sin datos de dispositivo.',
      };
  }
}

export function formatScheduleDays(days?: string[]): string {
  if (!days || days.length === 0) return '—';
  if (days.length === 7) return 'Todos los días';
  return days.join(', ');
}

export function formatScheduleCompareLine(
  schedule?: ScheduleCompareData | null
): string {
  if (!schedule) return '—';
  const days = formatScheduleDays(schedule.daysOfWeek);
  const target =
    schedule.relay != null
      ? `Relé ${schedule.relay}`
      : schedule.valve != null
        ? `Válvula ${schedule.valve}`
        : null;
  const enabled =
    schedule.enabled === undefined
      ? null
      : schedule.enabled
        ? 'Habilitada'
        : 'Deshabilitada';
  return [ `${schedule.startTime} – ${schedule.endTime}`, days, target, enabled ]
    .filter(Boolean)
    .join(' · ');
}

function normalizeDays(days?: string[]): string {
  return [...(days ?? [])].map((d) => d.toLowerCase()).sort().join(',');
}

export type CompareFieldKey =
  | 'time'
  | 'daysOfWeek'
  | 'target'
  | 'enabled';

export function getDifferingFields(
  platform?: ScheduleCompareData | null,
  device?: ScheduleCompareData | null
): Set<CompareFieldKey> {
  const diffs = new Set<CompareFieldKey>();
  if (!platform || !device) return diffs;

  if (
    platform.startTime !== device.startTime ||
    platform.endTime !== device.endTime
  ) {
    diffs.add('time');
  }
  if (normalizeDays(platform.daysOfWeek) !== normalizeDays(device.daysOfWeek)) {
    diffs.add('daysOfWeek');
  }

  const pTarget = platform.relay ?? platform.valve;
  const dTarget = device.relay ?? device.valve;
  if (pTarget !== dTarget) {
    diffs.add('target');
  }

  if (
    platform.enabled !== undefined &&
    device.enabled !== undefined &&
    platform.enabled !== device.enabled
  ) {
    diffs.add('enabled');
  }

  return diffs;
}

export function partitionVerificationItems(items: ScheduleVerificationItem[]) {
  const issues: ScheduleVerificationItem[] = [];
  const inProgress: ScheduleVerificationItem[] = [];
  const verified: ScheduleVerificationItem[] = [];

  for (const item of items) {
    if (isVerificationIssueStatus(item.verificationStatus)) {
      issues.push(item);
    } else if (isVerificationInProgressStatus(item.verificationStatus)) {
      inProgress.push(item);
    } else if (isVerificationOkStatus(item.verificationStatus)) {
      verified.push(item);
    } else {
      inProgress.push(item);
    }
  }

  return { issues, inProgress, verified };
}
